import React, { useRef, useState } from 'react';
import { translations } from '../translations/translations';
import { Connector } from '../tasks/types';
import { base64SvgToSrc } from './utils';
import { ExEmptyState, ExInput } from '@boomi/exosphere';

import './simple-slider.css';
import BetaConnectorRibbon from './BetaConnectorRibbon';

interface Props {
    sliderItems: Record<string, Connector>;
    itemType: 'action' | 'trigger';
    isMultiSelect: boolean;
    selectedItems?: Connector[] | undefined; // array because we could have multiselect on
    onSelect: (item: Connector) => void;
    language: string;
    showCount?: boolean;
}

const SimpleSlider = ({
    sliderItems,
    itemType,
    isMultiSelect,
    selectedItems,
    onSelect,
    language = 'en',
    showCount = true,
}: Props) => {
    const sliderUUID = useRef(crypto.randomUUID());
    const [search, setSearch] = useState('');

    const filteredItems = Object.values(sliderItems).filter((item) =>
        item.displayName.toUpperCase().includes(search.toUpperCase()),
    );

    const getUniqueItems = (items: Connector[], sortAZ: boolean) => {
        const unique = [...new Map(items.map((item) => [item.id, item])).values()];
        const sorted = unique.sort((a, b) => {
            const nameA = a.displayName.toUpperCase();
            const nameB = b.displayName.toUpperCase();

            if (nameA < nameB) {
                return -1;
            }

            if (nameA > nameB) {
                return 1;
            }

            // names are equal (should not really happen)
            return 0;
        });

        return sortAZ ? sorted : unique;
    };

    // Always show any items that have already been selected.
    // Selected item(s) will stay at original position if no filtering is applied,
    // otherwise they will appear in alphabetical order.
    // If sorting is false the selected items will appear at the start of the list.
    const listItems =
        selectedItems?.length && !!search
            ? getUniqueItems(selectedItems.concat(filteredItems), true)
            : filteredItems;

    // Calculate the required number of grid columns so that we can still fit all our items within max two grid rows.
    const calcGridColumns = () => {
        const noOfItems = listItems.filter(
            (connector) =>
                !!Object.values(itemType === 'trigger' ? connector.triggers : connector.actions)
                    .length,
        ).length;

        return Math.ceil(noOfItems / 2);
    };

    return (
        <section className="simple-slider">
            {/* SEARCH */}
            <ExInput
                data-testId="sliderSearchInput"
                className="exo-search-input"
                placeholder={translations[language].COMMON_search_placeholder}
                input-id="default"
                type="text"
                footertype="info"
                inputmode="text"
                size="default"
                flavor="default"
                leadingicon="magnifying-glass"
                removemarginbottom
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearch(e.currentTarget.value)
                }
            />

            {/* LIST OF ITEMS (CONNECTORS) */}
            <div className="simple-slider-container">
                {listItems.length ? (
                    <ul
                        className="simple-slider-list"
                        data-testid={`${itemType}_list`}
                        // Using the calculated number of grid columns, so that we can still have max two rows
                        // in the grid while keeping a more natural tabbing order. Using this approach because
                        // the pure CSS setup can't give us the desired tabbing order.
                        style={{
                            gridTemplateColumns: `repeat(${calcGridColumns()}, auto)`,
                        }}
                    >
                        {listItems.map((connector, index) =>
                            Object.values(
                                itemType === 'trigger' ? connector.triggers : connector.actions,
                            ).length ? (
                                <li key={index}>
                                    <label
                                        className="app-tile"
                                        title={connector.description}
                                        role="button"
                                        htmlFor={`${itemType}_${connector.id}_${sliderUUID.current}`}
                                    >
                                        <BetaConnectorRibbon id={connector.id} />
                                        <div className="app-tile-icon-wrapper">
                                            <img
                                                className="app-tile-icon"
                                                src={base64SvgToSrc(connector.icon)}
                                                alt={connector.displayName}
                                            />
                                        </div>
                                        <span className="app-tile-text">
                                            {connector.displayName}
                                        </span>
                                        <input
                                            className="app-tile-input"
                                            type={isMultiSelect ? 'checkbox' : 'radio'}
                                            onChange={() => onSelect(connector)}
                                            name={`taskConnectorSet_${sliderUUID.current}`}
                                            id={`${itemType}_${connector.id}_${sliderUUID.current}`}
                                            checked={selectedItems?.some(
                                                (item) => item.id === connector.id,
                                            )}
                                        />
                                    </label>
                                </li>
                            ) : null,
                        )}
                    </ul>
                ) : (
                    <ExEmptyState
                        className="empty-state"
                        icon="community"
                        label={translations[language].COMMON_no_results}
                        text={translations[language].COMMON_search_hint}
                    />
                )}
            </div>

            {/* SELECTED ITEMS COUNTER */}
            {showCount && (
                <div className="simple-slider-count">
                    <p className="text sm">
                        {translations[language].ONBOARDING_selected_count} {selectedItems?.length}
                    </p>
                </div>
            )}
        </section>
    );
};
export default SimpleSlider;
